import type { RouteRecordRaw } from 'vue-router'

/**
 * 仅规范化/合并 meta，保留 component / beforeEnter / props 等原始函数引用；
 * 为规避 RouteRecordRaw 联合类型导致的“不可赋值”告警，构建时用 any 承载，最终断言。
 */
export function normalizeRoutesMeta(
  routes: Readonly<RouteRecordRaw[]>,
  defaults: Record<string, any> = {},
): RouteRecordRaw[] {
  const normalizeMeta = (meta: any): any => {
    const m = meta || {}
    // 别名归一（旧 -> 新）
    const hideInMenuAliased = m.hideInMenu ?? m.hiddenInMenu ?? false
    const rawOrder = m.order ?? m.orderIndex

    // order 类型修正（字符串数值→数值；默认 99）
    const orderNum =
      typeof rawOrder === 'number'
        ? rawOrder
        : typeof rawOrder === 'string' && !Number.isNaN(Number(rawOrder))
          ? Number(rawOrder)
          : 99

    // 合并默认值 → 再覆盖 → 再应用归一结果
    const normalized: any = {
      ...defaults,
      ...m,
      hideInMenu: hideInMenuAliased === true,
      order: orderNum,
    }

    // 删除旧键，避免后续消费方误用
    delete normalized.orderIndex
    delete normalized.hiddenInMenu

    return normalized
  }

  const walk = (nodes: Readonly<RouteRecordRaw[]>): RouteRecordRaw[] =>
    nodes.map((r) => {
      const { children, meta, ...rest } = r as any

      const base: any = {
        ...rest, // 保留原始 component/components/redirect/props...
        meta: normalizeMeta(meta), // 只处理 meta
      }

      if (Array.isArray(children) && children.length) {
        base.children = walk(children as Readonly<RouteRecordRaw[]>)
      }

      return base as RouteRecordRaw
    })

  return walk(routes)
}

export default normalizeRoutesMeta
